import { useEffect } from 'react';
import { Card, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import {
  updateQueryStringOnFieldChange,
  updateQueryStringWithParams,
} from '@components/Filters/Filters';
import DefaultSpinner from '@components/Loader/Loader';
import { checkIsReady } from '@features/queryUtils';
import { countryApi } from '@reducers/geo';
import { clearPlanInfo, planApi } from '@reducers/plan/plan';
import { skipToken } from '@reduxjs/toolkit/query';

const schema = Yup.object({
  country: Yup.string().required(),
  typeOfService: Yup.string().required(),
});
const AutoSubmitFilter = ({ setCountryName, countries }) => {
  const formik = useFormikContext();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!formik.values.country) {
      formik.setFieldValue('typeOfService', []);
      return;
    }
    setCountryName(
      countries.data?.filter(
        ({ value }) => String(value) === formik.values.country
      )[0]?.name
    );
  }, [formik.values.country, countries.data]);
  useEffect(() => {
    if (!formik.values.country || !formik.values.typeOfService) {
      formik.setFieldValue('didType', []);
    }
  }, [formik.values.typeOfService]);

  useEffect(() => {
    if (location?.state?.fromCoverage) {
      return;
    }
    dispatch(clearPlanInfo());
  }, [formik.values]);
  return null;
};

const OrderNumbersFilter = ({ setCountryName }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const countries = countryApi.useGetWithAvailablePlansForSelectQuery();
  const skipQuery = !searchParams.get('country');
  const tos = countryApi.useGetTosQuery(
    skipQuery ? skipToken : { country: searchParams.get('country') }
  );
  const didType = countryApi.useGetDidTypesQuery(
    searchParams.get('country')
      ? { id: searchParams.get('country') }
      : skipToken
  );
  const plans = planApi.useGetListQuery(
    skipQuery
      ? skipToken
      : {
          country: searchParams.get('country'),
          tos: searchParams.getAll('typeOfService'),
          type: searchParams.getAll('didType'),
        }
  );
  const isReady = {
    plan: checkIsReady(plans),
    tos: checkIsReady(tos),
    didType: checkIsReady(didType),
  };

  return (
    <Card>
      <Card.Header className="bg-didxl text-white">Search form</Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize
          validationSchema={schema}
          initialValues={{
            country: searchParams.get('country') || '',
            typeOfService: searchParams.getAll('typeOfService') || [],
            didType: searchParams.getAll('didType') || [],
            area: searchParams.get('area') || '',
            areaCode: searchParams.get('areaCode') || '',
          }}
          onSubmit={() => {}}
        >
          {({ handleChange, handleBlur, values }) => (
            <Form>
              <Form.Group className="mb-3" controlId="formGroupCountry">
                <Form.Label>Country</Form.Label>
                <Form.Select
                  name="country"
                  size="sm"
                  value={values.country}
                  onChange={(e) => {
                    updateQueryStringOnFieldChange(e, {
                      clearFields: [
                        'didType',
                        'typeOfService',
                        'prefix',
                        'prefixName',
                      ],
                      searchParams,
                      setSearchParams,
                    });
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  disabled={countries.isLoading}
                >
                  <option value="">Select country</option>
                  {!countries.isLoading &&
                  !countries.isError &&
                  countries.data.length > 0 ? (
                    <>
                      {countries.data.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.name}
                        </option>
                      ))}
                    </>
                  ) : null}
                </Form.Select>
              </Form.Group>
              <hr className="textOrange" />
              {isReady.didType && Object.keys(didType.data).length > 0 ? (
                <Form.Group className="mb-3">
                  <Form.Label>Number types:</Form.Label>
                  <>
                    {Object.entries(didType.data).map(([id, item]) => (
                      <Form.Check
                        key={id}
                        type="checkbox"
                        name="didType"
                        label={item.description}
                        size="sm"
                        value={id}
                        checked={values.didType.includes(String(id))}
                        disabled={!item.available}
                        onChange={(e) => {
                          updateQueryStringWithParams(
                            e.target.name,
                            e.target.value,
                            { searchParams, setSearchParams }
                          );
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                      />
                    ))}
                  </>
                </Form.Group>
              ) : null}
              {isReady.tos && Object.keys(tos.data).length > 0 ? (
                <Form.Group className="mb-3">
                  <Form.Label>Supported services:</Form.Label>
                  <>
                    {Object.entries(tos.data).map(([id, item]) => (
                      <Form.Check
                        key={id}
                        type="checkbox"
                        name="typeOfService"
                        label={item.description}
                        size="sm"
                        value={id}
                        checked={values.typeOfService.includes(String(id))}
                        disabled={!item.available}
                        onChange={(e) => {
                          updateQueryStringWithParams(
                            e.target.name,
                            e.target.value,
                            { searchParams, setSearchParams }
                          );
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                      />
                    ))}
                  </>
                </Form.Group>
              ) : null}
              {tos.isLoading ||
              tos.isFetching ||
              didType.isLoading ||
              didType.isFetching ? (
                <DefaultSpinner />
              ) : null}
              <AutoSubmitFilter
                setCountryName={setCountryName}
                countries={countries}
              />
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default OrderNumbersFilter;
